import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';
import Image from 'next/image';
import YouTube from 'react-youtube';

import { Box, Divider, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { ShapesBanner } from '~/components/shapes-banner';
import { TemplateButton } from '~/components/template-button';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { getEnterpriseVideoContent } from '~/features/enterprise-resource/controllers/get-enterprise-video-content';
import { EnterpriseVideoContent } from '~/features/enterprise-resource/models/enterprise-video-content';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { ResourceCard } from '~/features/page-template/templates/enterprise-resource-center/components/resource-card';
import { convertResourcesToStandardFormat } from '~/features/page-template/templates/enterprise-resource-center/utils';

type EnterpriseVideoPageProps = {
  page: Page;
  enterpriseVideoContent: EnterpriseVideoContent;
};

export default function EnterpriseVideoPage({ page, enterpriseVideoContent }: EnterpriseVideoPageProps) {
  const videoData = enterpriseVideoContent.videoData;

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.EnterpriseVideo} />
      <PageLayout withoutVerticalPadding>
        <Box bg='blue.800' display='flex' py={{ base: '40px', lg: '80px' }}>
          <ContentContainer>
            <Text color='accent.lime' size='label'>
              Video
            </Text>
            <Box h='24px' />
            <Image
              alt='Udacity Accenture logo'
              height={22}
              quality={100}
              src='/images/logos/udacity-accenture.svg'
              width={124}
              priority
            />
            <Divider borderColor='accent.sky.500' marginY='40px' />
            <Box display='flex' flexWrap={{ base: 'wrap', lg: 'nowrap' }}>
              <Heading as='h2' color='white' size='marketing-4xl'>
                {videoData.title}
              </Heading>
            </Box>
            <Box h='24px' />
            <YouTube
              iframeClassName='youtube-container'
              title={videoData.video.title}
              videoId={videoData.video.youTubeVideoId}
              opts={{
                host: 'https://www.youtube-nocookie.com',
                playerVars: {
                  rel: 0,
                },
              }}
              onPlay={() =>
                trackEvent({
                  name: 'Video Played',
                  type: 'click',
                  label: videoData.title,
                })
              }
            />
            <Box h='40px' />

            <Box color='white' sx={{ 'a, a:visited': { color: 'white' } }}>
              <Markdown markdownText={videoData.description} />
            </Box>

            {videoData.primaryCta && (
              <TemplateButton
                {...videoData.primaryCta}
                marginTop='24px'
                onClick={() =>
                  trackEvent({
                    name: 'Video Page CTA Clicked',
                    type: 'click',
                    label: videoData.primaryCta?.buttonLabel ?? '',
                    value: 'cta 1',
                  })
                }
              />
            )}
          </ContentContainer>
        </Box>
        {!!videoData.resources?.length && (
          <ContentContainer paddingY={{ base: '40px', lg: '80px' }}>
            <Heading marginBottom='24px' size='marketing-4xl'>
              Related Resources
            </Heading>
            <Box display='flex' flexDirection={{ base: 'column', lg: 'row' }} gap='32px'>
              {videoData.resources.map((resource) => {
                if (!resource) return null;
                return (
                  <Box key={resource._id} flex={1}>
                    <ResourceCard imagePriority={true} resource={convertResourcesToStandardFormat([resource])[0]} />
                  </Box>
                );
              })}
            </Box>
          </ContentContainer>
        )}
        {videoData.shapesBanner && (
          <ShapesBanner
            {...videoData.shapesBanner}
            flipTitleAndSubtitleOrder={true}
            onPrimaryCtaClick={() =>
              trackEvent({
                name: 'Video Page CTA Clicked',
                type: 'click',
                label: videoData.shapesBanner?.primaryCta?.buttonLabel ?? '',
                value: 'cta 2',
              })
            }
            onSecondaryCtaClick={() =>
              trackEvent({
                name: 'Video Page CTA Clicked',
                type: 'click',
                label: videoData.shapesBanner?.secondaryCta?.buttonLabel ?? '',
                value: 'cta 3',
              })
            }
          />
        )}
      </PageLayout>
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    fallback: 'blocking',
    paths: [],
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{ slug: string }>): Promise<GetStaticPropsResult<EnterpriseVideoPageProps>> {
  const slug = params?.slug;

  if (!slug) {
    logger.fatal('Enterprise resource slug not found', {
      file: 'src/pages/video/[slug].tsx',
      function: 'getStaticProps',
      slug,
    });

    throw new Error('Enterprise resource slug not found');
  }

  const [page, enterpriseVideoContent] = await Promise.all([getPage('/video'), getEnterpriseVideoContent(slug)]);

  if (!enterpriseVideoContent) {
    return {
      notFound: true,
    };
  }

  // If enterpriseVideoContent has seo, override default page seo
  if (enterpriseVideoContent.seo) {
    page.seo = enterpriseVideoContent.seo;
  }

  return {
    props: {
      page,
      enterpriseVideoContent,
    },
  };
}
